'use client';

import { c as _c } from "react/compiler-runtime";
import { type WeightUnit, WeightUnitSchema } from '@repo/schema/load';
import { TEST_IDS } from '@repo/sdk/constants';
import { formatWeight } from '@repo/sdk/helpers';
import { Button, ButtonGroup, Field, FieldError, FieldLabel, Input, Select, SelectContent, SelectItem, SelectPortal, SelectTrigger, SelectValue } from '@repo/ui/components';
import { Minus, Plus } from '@repo/ui/icons';
import { cn } from '@repo/ui/lib/utils';
import { useStore } from '@tanstack/react-form';
import { ChangeEvent, type ReactNode, type Ref, useRef, useState } from 'react';
import { ReadOnlyField, useReadOnly } from '../../components/readonly.tsx';
import { useFieldContext } from '../../lib/form-context.ts';
import { DEFAULT_WEIGHT_UNIT, sanitizeWeightInput, toDisplayValue, toKg } from './weight-input.helpers.ts';
const WEIGHT_OPTIONS: Array<{
  label: string;
  multiplier: number;
  value: WeightUnit;
}> = [{
  label: 'tonnes',
  multiplier: 1000,
  value: 't'
}, {
  label: 'kilograms',
  multiplier: 1,
  value: 'kg'
}];
interface WeightInputProps {
  autoFocus?: boolean;
  className?: string;
  /** Render the numeric input without the increment/decrement controls. */
  compact?: boolean;
  inputRef?: Ref<HTMLInputElement>;
  label?: ReactNode;
  onBlur?: () => void;
  /**
   * The unit the weight was entered in. The owning form holds it, so the stored
   * value (always kg) renders back in the unit the user chose. Required: without
   * them the unit select cannot persist a choice.
   */
  onUnitChange: (unit: WeightUnit) => void;
  /** Render the unit menu outside an overflow-clipped dialog surface. */
  portalUnitSelect?: boolean;
  unit: null | WeightUnit;
}
export const WeightInput = t0 => {
  const $ = _c(78);
  const {
    autoFocus,
    className,
    compact: t1,
    inputRef,
    label: t2,
    onBlur,
    onUnitChange,
    portalUnitSelect: t3,
    unit
  } = t0;
  const compact = t1 === undefined ? false : t1;
  const label = t2 === undefined ? "Weight" : t2;
  const portalUnitSelect = t3 === undefined ? false : t3;
  const field = useFieldContext();
  const blurTimeoutRef = useRef(null);
  const [draft, setDraft] = useState(null);
  const errors = useStore(field.store, _temp);
  const displayUnit = unit ?? DEFAULT_WEIGHT_UNIT;
  const weightInKg = useStore(field.store, _temp2);
  let t4;
  if ($[0] !== displayUnit) {
    t4 = WEIGHT_OPTIONS.find(option => option.value === displayUnit);
    $[0] = displayUnit;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const selectedUnit = t4;
  let t5;
  if ($[2] !== displayUnit || $[3] !== draft || $[4] !== weightInKg) {
    t5 = draft ?? toDisplayValue(weightInKg, displayUnit);
    $[2] = displayUnit;
    $[3] = draft;
    $[4] = weightInKg;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const displayValue = t5;
  const isReadOnly = useReadOnly();
  let t6;
  if ($[6] !== displayUnit || $[7] !== onUnitChange || $[8] !== unit) {
    t6 = () => {
      if (unit === null) {
        onUnitChange(displayUnit);
      }
    };
    $[6] = displayUnit;
    $[7] = onUnitChange;
    $[8] = unit;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const materialiseUnit = t6;
  let t7;
  if ($[10] !== field || $[11] !== materialiseUnit) {
    t7 = weight => {
      setDraft(null);
      materialiseUnit();
      field.handleChange(weight);
    };
    $[10] = field;
    $[11] = materialiseUnit;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const commit = t7;
  let t8;
  if ($[13] !== commit || $[14] !== selectedUnit.multiplier || $[15] !== weightInKg) {
    t8 = () => {
      commit((weightInKg ?? 0) + selectedUnit.multiplier);
    };
    $[13] = commit;
    $[14] = selectedUnit.multiplier;
    $[15] = weightInKg;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const handlePlus = t8;
  let t9;
  if ($[17] !== commit || $[18] !== selectedUnit.multiplier || $[19] !== weightInKg) {
    t9 = () => {
      const next = (weightInKg ?? 0) - selectedUnit.multiplier;
      if (next < 0) {
        return;
      }
      commit(next);
    };
    $[17] = commit;
    $[18] = selectedUnit.multiplier;
    $[19] = weightInKg;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const handleMinus = t9;
  let t10;
  if ($[21] !== displayUnit || $[22] !== field || $[23] !== materialiseUnit) {
    t10 = event => {
      const inputValue = sanitizeWeightInput(event.target.value, displayUnit);
      if (inputValue === null) {
        return;
      }
      setDraft(inputValue);
      materialiseUnit();
      field.handleChange(toKg(inputValue, displayUnit));
    };
    $[21] = displayUnit;
    $[22] = field;
    $[23] = materialiseUnit;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const handleInputChange = t10;
  let t11;
  if ($[25] !== field || $[26] !== onBlur) {
    t11 = () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
      blurTimeoutRef.current = setTimeout(() => {
        const container = document.activeElement?.closest("[data-weight-container]");
        if (!container) {
          setDraft(null);
          field.handleBlur();
          onBlur?.();
        }
      }, 100);
    };
    $[25] = field;
    $[26] = onBlur;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const handleBlurWithDelay = t11;
  let t12;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }
    };
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const handleFocus = t12;
  if (isReadOnly) {
    let t13;
    if ($[29] !== label) {
      t13 = label && <FieldLabel>{label}</FieldLabel>;
      $[29] = label;
      $[30] = t13;
    } else {
      t13 = $[30];
    }
    let t14;
    if ($[31] !== unit || $[32] !== weightInKg) {
      t14 = formatWeight(weightInKg, unit);
      $[31] = unit;
      $[32] = weightInKg;
      $[33] = t14;
    } else {
      t14 = $[33];
    }
    let t15;
    if ($[34] !== t13 || $[35] !== t14) {
      t15 = <ReadOnlyField label={t13}>{t14}</ReadOnlyField>;
      $[34] = t13;
      $[35] = t14;
      $[36] = t15;
    } else {
      t15 = $[36];
    }
    return t15;
  }
  let t13;
  if ($[37] !== field.name || $[38] !== label) {
    t13 = label && <FieldLabel htmlFor={field.name}>{label}</FieldLabel>;
    $[37] = field.name;
    $[38] = label;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== className) {
    t14 = cn("w-fit!", className);
    $[40] = className;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== compact || $[43] !== handleMinus) {
    t15 = !compact && <Button data-testid={TEST_IDS.cargo.weight.minusButton} onClick={handleMinus} size="icon" type="button" variant="outline"><span className="sr-only">-</span><Minus /></Button>;
    $[42] = compact;
    $[43] = handleMinus;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  const t16 = displayUnit === "t" ? "decimal" : "numeric";
  let t17;
  if ($[45] !== autoFocus || $[46] !== displayValue || $[47] !== field.name || $[48] !== handleInputChange || $[49] !== inputRef || $[50] !== t16) {
    t17 = <Input autoFocus={autoFocus} className="w-16 text-center" data-testid={TEST_IDS.cargo.weight.input} id={field.name} inputMode={t16} onChange={handleInputChange} placeholder="0" ref={inputRef} value={displayValue} data-sentry-element="Input" data-sentry-source-file="weight-input.tsx" />;
    $[45] = autoFocus;
    $[46] = displayValue;
    $[47] = field.name;
    $[48] = handleInputChange;
    $[49] = inputRef;
    $[50] = t16;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  let t18;
  if ($[52] !== compact || $[53] !== handlePlus) {
    t18 = !compact && <Button data-testid={TEST_IDS.cargo.weight.plusButton} onClick={handlePlus} size="icon" type="button" variant="outline"><span className="sr-only">+</span><Plus /></Button>;
    $[52] = compact;
    $[53] = handlePlus;
    $[54] = t18;
  } else {
    t18 = $[54];
  }
  let t19;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = WEIGHT_OPTIONS.map(_temp3);
    $[55] = t19;
  } else {
    t19 = $[55];
  }
  let t20;
  if ($[56] !== onUnitChange) {
    t20 = value => {
      const parsed = WeightUnitSchema.safeParse(value);
      if (!parsed.success) {
        return;
      }
      setDraft(null);
      onUnitChange(parsed.data);
    };
    $[56] = onUnitChange;
    $[57] = t20;
  } else {
    t20 = $[57];
  }
  let t21;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <SelectTrigger data-testid={TEST_IDS.cargo.weight.unitSelect} data-sentry-element="SelectTrigger" data-sentry-source-file="weight-input.tsx"><SelectValue placeholder="Select unit" data-sentry-element="SelectValue" data-sentry-source-file="weight-input.tsx" /></SelectTrigger>;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  let t22;
  if ($[59] !== portalUnitSelect) {
    t22 = portalUnitSelect ? <SelectPortal><SelectContent>{WEIGHT_OPTIONS.map(_temp4)}</SelectContent></SelectPortal> : <SelectContent>{WEIGHT_OPTIONS.map(_temp5)}</SelectContent>;
    $[59] = portalUnitSelect;
    $[60] = t22;
  } else {
    t22 = $[60];
  }
  let t23;
  if ($[61] !== displayUnit || $[62] !== t20 || $[63] !== t22) {
    t23 = <Select items={t19} onValueChange={t20} value={displayUnit} data-sentry-element="Select" data-sentry-source-file="weight-input.tsx">{t21}{t22}</Select>;
    $[61] = displayUnit;
    $[62] = t20;
    $[63] = t22;
    $[64] = t23;
  } else {
    t23 = $[64];
  }
  let t24;
  if ($[65] !== handleBlurWithDelay || $[66] !== t14 || $[67] !== t15 || $[68] !== t17 || $[69] !== t18 || $[70] !== t23) {
    t24 = <ButtonGroup className={t14} data-weight-container={true} onBlur={handleBlurWithDelay} onFocus={handleFocus} data-sentry-element="ButtonGroup" data-sentry-source-file="weight-input.tsx">{t15}{t17}{t18}{t23}</ButtonGroup>;
    $[65] = handleBlurWithDelay;
    $[66] = t14;
    $[67] = t15;
    $[68] = t17;
    $[69] = t18;
    $[70] = t23;
    $[71] = t24;
  } else {
    t24 = $[71];
  }
  let t25;
  if ($[72] !== errors) {
    t25 = <FieldError className="text-sm" errors={errors} data-sentry-element="FieldError" data-sentry-source-file="weight-input.tsx" />;
    $[72] = errors;
    $[73] = t25;
  } else {
    t25 = $[73];
  }
  let t26;
  if ($[74] !== t13 || $[75] !== t24 || $[76] !== t25) {
    t26 = <Field className="gap-1" data-sentry-element="Field" data-sentry-component="WeightInput" data-sentry-source-file="weight-input.tsx">{t13}{t24}{t25}</Field>;
    $[74] = t13;
    $[75] = t24;
    $[76] = t25;
    $[77] = t26;
  } else {
    t26 = $[77];
  }
  return t26;
};
function _temp(store) {
  return store.meta.errors;
}
function _temp2(store_0) {
  return store_0.value;
}
function _temp3(weight_0) {
  return {
    label: weight_0.label,
    value: weight_0.value
  };
}
function _temp4(weight_1) {
  return <SelectItem key={weight_1.value} value={weight_1.value}>{weight_1.label}</SelectItem>;
}
function _temp5(weight_2) {
  return <SelectItem key={weight_2.value} value={weight_2.value}>{weight_2.label}</SelectItem>;
}